// Comandos do agregado Categoria Financeira (issue #56) — criar, editar,
// inativar/reativar, excluir. Dado puro; o executor só chama o domínio.
//
// O comando referencia `DadosCategoriaFinanceira` do domínio, não uma cópia, e
// o `validar` declara esse tipo como retorno: campo novo no domínio vira erro
// de compilação aqui (ADR-0013). `aplicaA` é união fechada — `umDe`.

import type { Snapshot } from "../domain/snapshot.ts";
import { APLICACOES_CATEGORIA } from "../domain/snapshot.ts";
import {
  criarCategoriaFinanceira,
  definirAtivoCategoriaFinanceira,
  editarCategoriaFinanceira,
  excluirCategoriaFinanceira,
  type DadosCategoriaFinanceira,
} from "../domain/categorias-financeiras.ts";
import { assertNunca } from "../lib/assert-nunca.ts";
import { comoObjeto, exigirBooleano, exigirTexto, texto, umDe } from "./_forma.ts";
import type { ContextoComando } from "./lotes.ts";

export type ComandoCategoriaFinanceira =
  | { tipo: "criar"; id: string; dados: DadosCategoriaFinanceira }
  | { tipo: "editar"; id: string; dados: DadosCategoriaFinanceira }
  | { tipo: "definirAtivo"; id: string; ativo: boolean }
  | { tipo: "excluir"; id: string };

export function executarComandoCategoriaFinanceira(
  snapshot: Snapshot,
  cmd: ComandoCategoriaFinanceira,
  _ctx: ContextoComando,
): Snapshot {
  switch (cmd.tipo) {
    case "criar":
      return criarCategoriaFinanceira(snapshot, cmd.dados, cmd.id);
    case "editar":
      return editarCategoriaFinanceira(snapshot, cmd.id, cmd.dados);
    case "definirAtivo":
      return definirAtivoCategoriaFinanceira(snapshot, cmd.id, cmd.ativo);
    case "excluir":
      return excluirCategoriaFinanceira(snapshot, cmd.id);
    default:
      return assertNunca(cmd);
  }
}

function validarDados(valor: unknown): DadosCategoriaFinanceira {
  const o = comoObjeto(valor, "Dados da categoria financeira");
  return {
    // `nome` o domínio valida (mensagem amigável), então passa cru como ""
    nome: texto(o.nome),
    subcategoria: texto(o.subcategoria),
    aplicaA: umDe(o.aplicaA, "aplicaA", APLICACOES_CATEGORIA),
  };
}

export function validarComandoCategoriaFinanceira(
  valor: unknown,
): ComandoCategoriaFinanceira {
  const c = comoObjeto(valor, "Comando de categoria financeira");
  switch (c.tipo) {
    case "criar":
      return {
        tipo: "criar",
        id: exigirTexto(c.id, "id"),
        dados: validarDados(c.dados),
      };
    case "editar":
      return {
        tipo: "editar",
        id: exigirTexto(c.id, "id"),
        dados: validarDados(c.dados),
      };
    case "definirAtivo":
      return {
        tipo: "definirAtivo",
        id: exigirTexto(c.id, "id"),
        ativo: exigirBooleano(c.ativo, "ativo"),
      };
    case "excluir":
      return { tipo: "excluir", id: exigirTexto(c.id, "id") };
    default:
      throw new Error(
        `Tipo de comando de categoria financeira desconhecido: ${String(c.tipo)}.`,
      );
  }
}
